import {
  CommonError,
  ErrorWithLabelMessage,
  InvoiceReportOrderByDirection,
  InvoiceReportOrderByField,
  SearchAdminInvoiceReportsQuery,
  SearchAdminInvoiceReportsQueryPaymentMethod,
  SearchInvoiceReportsQueryMfFile,
  SearchInvoiceReportsQueryPaymentStatus,
  SearchInvoiceReportsQueryStatus,
  SearchedAdminInvoiceReportPaymentStatus,
  SearchedInvoiceReportStatus,
} from '@/context/services/admin_invoice_reports/AdminInvoiceReportsQuery.enums';
import { ModernPaginationProps } from '@moneyforward/ap-frontend-components';
import { RefetchOptions } from '@tanstack/react-query';

import { Dayjs } from 'dayjs';

/** Action state from API: visible = show or not, allowed = enabled, reason = tooltip when disabled. */
export interface ReportActionState {
  visible: boolean;
  allowed: boolean;
  reason: string | null;
}

export interface AdminInvoiceReportColumn {
  [key: string]: unknown;
  id: string;
  number: string;
  created_at: string;
  submitted_at?: string | null;
  office_member: {
    name: string;
    number: string;
  };
  title: string;
  status: SearchedInvoiceReportStatus;
  is_waiting?: boolean;
  ap_payee_name?: string | null;
  book_date?: string | null;
  due_date?: string | null;
  total_amount?: string | null;
  foreign_currency_amount?: string | null;
  report_approval_names?: string | null;
  approved_at?: string | null;
  payment_date?: string | null;
  payment_status?: SearchedAdminInvoiceReportPaymentStatus;
  invoice_file?: {
    name: string;
    receipt_type: string;
  };
  error_messages?: ErrorWithLabelMessage[] | null;
  detail_path: string;
  edit_path: string;
  copy_path?: string;
  is_copyable?: boolean;
  edit_action: ReportActionState;
  delete_action: ReportActionState;
  cancel_action: ReportActionState;
  /** Share button state for this invoice report; undefined where the list API doesn't return it yet. */
  share_action?: ReportActionState;
  share_path?: string;
}

/**
 * Form state for the admin invoice reports search form.
 */
export interface AdminInvoiceReportsSearchFormData {
  // Text inputs
  query: string;
  number: string;
  // Status selects
  status: SearchInvoiceReportsQueryStatus | null;
  payment_status: SearchInvoiceReportsQueryPaymentStatus | null;
  mf_file: SearchInvoiceReportsQueryMfFile | null;
  // Multi-selects (dummy data — excluded from API query)
  applicant_ids: string[];
  applicant_department_ids: string[];
  department_ids: string[];
  viewer_ids: string[];
  // Multi-select (real — wired to API)
  ap_counterparty_ids: string[];
  ap_payee_ids: string[];
  // Single select (wired to API)
  payment_method: SearchAdminInvoiceReportsQueryPaymentMethod | null;
  // Amount range
  total_amount_from: string;
  total_amount_to: string;
  // Date ranges
  created_date: (string | Dayjs)[];
  submitted_date: (string | Dayjs)[];
  approved_date: (string | Dayjs)[];
  book_date: (string | Dayjs)[];
  due_date: (string | Dayjs)[];
  payment_date: (string | Dayjs)[];
  // Sort / pagination (passed through from table state)
  sort_fields: string[] | undefined;
  sort_orders: string[] | undefined;
  page: number;
}

export type AdminInvoiceReportsSearchQuery = SearchAdminInvoiceReportsQuery & {
  sort_fields?: string[];
  sort_orders?: string[];
  page?: number;
};

export type SortField =
  | 'number'
  | 'created_at'
  | 'submitted_at'
  | 'book_date'
  | 'due_date'
  | 'approved_at'
  | 'payment_date';

export type OrderByItem = {
  field: InvoiceReportOrderByField;
  direction: InvoiceReportOrderByDirection;
};

export type FetchAdminInvoiceReportsArgs = {
  page?: number;
  perPage?: number;
  query?: AdminInvoiceReportsSearchQuery;
  order_by?: OrderByItem[];
  onPageChange?: (page: number) => void;
};

export type AdminInvoiceReportsSearchRefetch = (
  options?: RefetchOptions
) => Promise<unknown>;

export type FetchAdminInvoiceReportsResult = {
  pagination: ModernPaginationProps;
  data: AdminInvoiceReportColumn[];
  refetch: AdminInvoiceReportsSearchRefetch;
  isFetching?: boolean;
  isError: boolean;
  error: CommonError | undefined;
};

export const SORT_FIELD_MAPPING: Record<SortField, InvoiceReportOrderByField> =
  {
    number: 'number',
    created_at: 'created_date',
    submitted_at: 'submitted_date',
    book_date: 'invoice_book_date',
    due_date: 'invoice_due_date',
    approved_at: 'approved_date',
    payment_date: 'payment_date',
  };

export const mapColumnKeyToApiSortField = (
  field: SortField
): InvoiceReportOrderByField => {
  return SORT_FIELD_MAPPING[field];
};

/** True when `key` is a valid table column key for sorting. */
export const isSortFieldKey = (key: string): key is SortField => {
  switch (key) {
    case 'number':
    case 'created_at':
    case 'submitted_at':
    case 'book_date':
    case 'due_date':
    case 'approved_at':
    case 'payment_date':
      return true;
    default:
      return false;
  }
};

/** Map API order_by field name back to table column key (for URL → table sort state). */
export const mapApiSortFieldToColumnKey = (
  apiField: InvoiceReportOrderByField
): SortField | null => {
  switch (apiField) {
    case 'number':
      return 'number';
    case 'created_date':
      return 'created_at';
    case 'submitted_date':
      return 'submitted_at';
    case 'invoice_book_date':
      return 'book_date';
    case 'invoice_due_date':
      return 'due_date';
    case 'approved_date':
      return 'approved_at';
    case 'payment_date':
      return 'payment_date';
    default:
      return null;
  }
};
